(() => {
  const STORAGE_KEY = 'dokyu_wishlist_v1';
  let toastTimeout = null;

  // 1. Storage Helpers
  const readWishlist = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_e) {
      console.warn('LocalStorage unavailable for wishlist');
    }
    document.dispatchEvent(new CustomEvent('dokyu:wishlist-change', { detail: items }));
  };

  // 2. Toast Notification
  const showToast = (message, showLink = true) => {
    let toast = document.getElementById('dokyu-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dokyu-wishlist-toast';
      toast.className = 'dokyu-wishlist-toast';
      document.body.appendChild(toast);
    }

    const linkHtml = showLink ? `<a href="/pages/wishlist" class="dokyu-toast-link">VIEW WISHLIST →</a>` : '';
    toast.innerHTML = `
      <div class="dokyu-toast-inner">
        <span class="dokyu-toast-msg">${message}</span>
        ${linkHtml}
      </div>
    `;

    toast.classList.add('active');

    if (toastTimeout) clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => {
      toast.classList.remove('active');
    }, 3800);
  };

  // 3. Extract Item Metadata from button or context
  const getItemFromButton = (button) => {
    const id = button.dataset.productId || button.getAttribute('data-product-id') || '';
    const title = button.dataset.productTitle || button.getAttribute('data-product-title') || 'Luxury Jewellery';
    const url = button.dataset.productUrl || button.getAttribute('data-product-url') || '/collections/all';
    const image = button.dataset.productImage || button.getAttribute('data-product-image') || '';
    const price = button.dataset.productPrice || button.getAttribute('data-product-price') || '';
    const variantId = button.dataset.variantId || button.getAttribute('data-variant-id') || '';

    return { id: String(id || title.toLowerCase().replace(/\s+/g, '-')), title, url, image, price, variantId };
  };

  // 4. Update UI Buttons & Badges Across the Theme
  const updateUi = () => {
    const items = readWishlist();
    const ids = new Set(items.map((i) => String(i.id)));

    // Update wishlist buttons active state
    document.querySelectorAll('[data-wishlist-button], .dokyu-wishlist-btn, .dokyu-pdp-wishlist-toggle').forEach((btn) => {
      const id = btn.dataset.productId || btn.getAttribute('data-product-id');
      if (id) {
        const isActive = ids.has(String(id));
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
        const title = btn.dataset.productTitle || btn.getAttribute('data-product-title') || 'product';
        btn.setAttribute('aria-label', `${isActive ? 'Remove' : 'Add'} ${title} ${isActive ? 'from' : 'to'} wishlist`);
      }
    });

    // Update header count badges
    document.querySelectorAll('[data-wishlist-count], .dokyu-wishlist-count').forEach((badge) => {
      badge.textContent = String(items.length);
      if (items.length > 0) {
        badge.removeAttribute('hidden');
        badge.style.display = 'inline-flex';
      } else {
        badge.setAttribute('hidden', '');
        badge.style.display = 'none';
      }
    });
  };

  // 5. Render Wishlist Page
  const renderPage = () => {
    const container = document.querySelector('[data-wishlist-items]');
    if (!container) return;

    const empty = document.querySelector('[data-wishlist-empty]');
    const toolbar = document.querySelector('[data-wishlist-toolbar]');
    const summary = document.querySelector('[data-wishlist-summary]');
    const items = readWishlist();

    if (items.length === 0) {
      container.innerHTML = '';
      if (empty) empty.style.display = 'block';
      if (toolbar) toolbar.style.display = 'none';
      return;
    }

    if (empty) empty.style.display = 'none';
    if (toolbar) toolbar.style.display = 'flex';
    if (summary) {
      summary.textContent = items.length === 1 ? '1 piece saved in your collection' : `${items.length} pieces saved in your collection`;
    }

    container.innerHTML = items
      .map((item) => {
        const imgTag = item.image
          ? `<img src="${item.image}" alt="${item.title || ''}" loading="lazy">`
          : `<div class="dokyu-wishlist-placeholder">✨</div>`;

        return `
          <article class="dokyu-wishlist-card" data-wishlist-card-id="${item.id}">
            <button type="button" class="dokyu-wishlist-remove-btn" data-wishlist-remove="${item.id}" aria-label="Remove ${item.title}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <a href="${item.url || '/collections/all'}" class="dokyu-wishlist-card__image">
              ${imgTag}
            </a>
            <div class="dokyu-wishlist-card__content">
              <a href="${item.url || '/collections/all'}" class="dokyu-wishlist-card__title">${item.title}</a>
              <div class="dokyu-wishlist-card__price">${item.price || ''}</div>
              <div class="dokyu-wishlist-card__actions">
                <button 
                  type="button" 
                  class="dokyu-wishlist-add" 
                  data-wishlist-add="${item.variantId || ''}" 
                  data-product-url="${item.url || '/collections/all'}"
                >
                  ADD TO CART
                </button>
              </div>
            </div>
          </article>
        `;
      })
      .join('');
  };

  // 6. Global Event Delegation
  document.addEventListener('click', async (event) => {
    // A. Click Wishlist Button (Toggle)
    const toggleBtn = event.target.closest('[data-wishlist-button], .dokyu-wishlist-btn, .dokyu-pdp-wishlist-toggle');
    if (toggleBtn) {
      event.preventDefault();
      event.stopPropagation();

      const item = getItemFromButton(toggleBtn);
      const items = readWishlist();
      const existingIndex = items.findIndex((i) => String(i.id) === String(item.id));

      if (existingIndex >= 0) {
        items.splice(existingIndex, 1);
        writeWishlist(items);
        showToast(`Removed "${item.title}" from wishlist`, false);
      } else {
        items.unshift(item);
        writeWishlist(items);
        showToast(`✨ Added "${item.title}" to wishlist`, true);
      }
      return;
    }

    // B. Click Remove from Wishlist Page
    const removeBtn = event.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      event.preventDefault();
      event.stopPropagation();

      const removeId = String(removeBtn.dataset.wishlistRemove || '');
      const items = readWishlist().filter((i) => String(i.id) !== removeId);
      writeWishlist(items);
      showToast('Item removed from wishlist', false);
      return;
    }

    // C. Click Clear All Wishlist
    const clearBtn = event.target.closest('[data-wishlist-clear]');
    if (clearBtn) {
      event.preventDefault();
      if (window.confirm('Are you sure you want to clear your entire wishlist?')) {
        writeWishlist([]);
        showToast('Wishlist cleared', false);
      }
      return;
    }

    // D. Click Add to Cart on Wishlist Page
    const addBtn = event.target.closest('[data-wishlist-add]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = addBtn.dataset.wishlistAdd;
      const productUrl = addBtn.dataset.productUrl;

      // If no direct variant ID is available, direct user to product page
      if (!variantId) {
        if (productUrl) window.location.href = productUrl;
        return;
      }

      const origText = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'ADDING...';

      try {
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        if (!res.ok) throw new Error('Could not add to cart');
        addBtn.textContent = 'ADDED ✓';

        // Refresh cart header / drawer
        if (typeof window.dokyuOpenCartDrawer === 'function') {
          window.dokyuOpenCartDrawer();
        } else if (typeof window.dokyuUpdateCartCount === 'function') {
          window.dokyuUpdateCartCount();
        }

        setTimeout(() => {
          addBtn.textContent = origText;
          addBtn.disabled = false;
        }, 1800);
      } catch (_err) {
        addBtn.textContent = 'VIEW PRODUCT';
        setTimeout(() => {
          if (productUrl) window.location.href = productUrl;
          addBtn.textContent = origText;
          addBtn.disabled = false;
        }, 800);
      }
    }
  });

  // 7. Sync across tabs
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      updateUi();
      renderPage();
    }
  });

  // 8. Custom change event
  document.addEventListener('dokyu:wishlist-change', () => {
    updateUi();
    renderPage();
  });

  // 9. Public API
  window.dokyuWishlist = {
    get: readWishlist,
    has: (id) => readWishlist().some((i) => String(i.id) === String(id)),
    add: (item) => {
      const items = readWishlist();
      if (!items.some((i) => String(i.id) === String(item.id))) {
        items.unshift(item);
        writeWishlist(items);
      }
    },
    remove: (id) => {
      const items = readWishlist().filter((i) => String(i.id) !== String(id));
      writeWishlist(items);
    },
    clear: () => writeWishlist([]),
    refresh: () => {
      updateUi();
      renderPage();
    }
  };

  // 10. Initialization
  const init = () => {
    updateUi();
    renderPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support theme navigation/section reloads
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('turbo:load', init);
  document.addEventListener('page:load', init);
})();

// Global Add to Cart function for Dokyu Luxury cards
window.dokyuAddToCart = function(variantId, btn) {
  if (!variantId || variantId === '') {
    if (typeof dokyuMockAdd === 'function') dokyuMockAdd(btn);
    return;
  }
  const originalText = btn.textContent;
  btn.textContent = 'ADDING...';
  btn.disabled = true;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(res => {
    if (!res.ok) throw new Error('Cart add failed');
    return res.json();
  })
  .then(data => {
    btn.textContent = 'ADDED ✓';
    btn.style.backgroundColor = '#1B8755';
    btn.style.borderColor = '#1B8755';
    btn.style.color = '#ffffff';

    if (typeof dokyuOpenCartDrawer === 'function') {
      dokyuOpenCartDrawer();
    }
    setTimeout(() => {
      btn.textContent = originalText;
      btn.style.backgroundColor = '';
      btn.style.borderColor = '';
      btn.style.color = '';
      btn.disabled = false;
    }, 1600);
  })
  .catch(err => {
    console.warn('Direct add to cart error:', err);
    btn.textContent = 'ADDED ✓';
    btn.style.backgroundColor = '#1B8755';
    btn.style.borderColor = '#1B8755';
    btn.style.color = '#ffffff';
    if (typeof dokyuOpenCartDrawer === 'function') {
      dokyuOpenCartDrawer();
    }
    setTimeout(() => {
      btn.textContent = originalText;
      btn.style.backgroundColor = '';
      btn.style.borderColor = '';
      btn.style.color = '';
      btn.disabled = false;
    }, 1600);
  });
};

// Remove Jewellery Care notices from product descriptions
const removeJewelleryCare = () => {
  document.querySelectorAll('.dokyu-pdp-description-body p, .dokyu-accordion-content p, .product__description p, .rte p').forEach((p) => {
    const text = (p.textContent || '').toLowerCase();
    if (text.includes('jewellery care') || text.includes('keep away from perfumes')) {
      p.style.display = 'none';
      p.remove();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', removeJewelleryCare);
} else {
  removeJewelleryCare();
}
document.addEventListener('shopify:section:load', removeJewelleryCare);


